const toDateSafe = (v) => {
  if (!v) return null;
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? null : d;
};

export const normalizeStatus = (status) => {
  const s = String(status || '').toUpperCase();
  if (s === 'SUCCESS' || s === 'COMPLETED') return 'success';
  if (s === 'ERROR' || s === 'FAILED') return 'error';
  if (s === 'RUNNING') return 'running';
  if (s === 'SKIPPED' || s === 'NOT_EXECUTED' || s === 'NOT EXECUTED') return 'skipped';
  return s ? s.toLowerCase() : 'unknown';
};

const hasErrorEvent = (events) => {
  const list = Array.isArray(events) ? events : [];
  return list.some((e) => {
    const name =
      typeof e === 'string' ? e : (e?.name || e?.event || e?.type || '');
    const n = String(name || '').toLowerCase();
    return n === 'agent_execute_error' || n === 'request_failed';
  });
};

const hasCompleteEvent = (events) => {
  const list = Array.isArray(events) ? events : [];
  return list.some((e) => {
    const name =
      typeof e === 'string' ? e : (e?.name || e?.event || e?.type || '');
    const n = String(name || '').toLowerCase();
    return n === 'agent_execute_complete';
  });
};

const deriveStatusFromMessages = (msgs, fallback = 'running') => {
  const list = Array.isArray(msgs) ? msgs : [];
  if (!list.length) return 'skipped';
  const events = list.map((m) => m?.event).filter(Boolean);

  const hasError =
    hasErrorEvent(events) ||
    list.some((m) => normalizeStatus(m?.execStatus || m?.status) === 'error') ||
    list.some((m) => m?.error || m?.data?.error);
  if (hasError) return 'error';

  const hasSuccessEvent = events.some((e) => {
    const n = normalizeEventKey(e);
    return (
      n === 'agent_execute_complete' ||
      n === 'agent_goal_satisfied' ||
      n === 'goal_satisfied' ||
      n === 'agent_iteration_complete'
    );
  });
  if (hasSuccessEvent) return 'success';
  return normalizeStatus(fallback);
};

const firstKey = (obj) => {
  if (!obj || typeof obj !== 'object') return null;
  const keys = Object.keys(obj);
  return keys.length ? keys[0] : null;
};

/** --- Exec message ordering & retry segmentation (shared with ExecutionHistoryView) --- */

export const normalizeEventKey = (eventName) => String(eventName || '').trim().toLowerCase();

export const EVENT_PHASE_ORDER = {
  agent_execute_start: 0,
  agent_execute: 1,
  agent_llm_execute: 2,
  agent_verify_goal_usage: 3,
  agent_iteration_complete: 4,
  agent_execute_error: 5,
  request_failed: 6,
  agent_execute_complete: 7
};

export const sliceExecutionWindow = (messages) => {
  if (!Array.isArray(messages) || messages.length === 0) return [];
  const startIdx = messages.findIndex((m) => normalizeEventKey(m?.event) === 'agent_execute_start');
  const endIdx = (() => {
    for (let i = messages.length - 1; i >= 0; i -= 1) {
      if (normalizeEventKey(messages[i]?.event) === 'agent_execute_complete') return i;
    }
    return -1;
  })();

  if (startIdx === -1 && endIdx === -1) return messages;
  if (startIdx === -1) return messages.slice(0, endIdx + 1);
  if (endIdx === -1) return messages.slice(startIdx);
  if (endIdx < startIdx) return messages;
  return messages.slice(startIdx, endIdx + 1);
};

export const orderExecMessages = (messages) => {
  if (!Array.isArray(messages) || messages.length <= 1) return messages || [];

  const phaseOrder = EVENT_PHASE_ORDER;

  const startIdx = messages.findIndex((m) => normalizeEventKey(m?.event) === 'agent_execute_start');
  const completeIdx = (() => {
    for (let i = messages.length - 1; i >= 0; i -= 1) {
      if (normalizeEventKey(messages[i]?.event) === 'agent_execute_complete') return i;
    }
    return -1;
  })();
  const looksChronological =
    startIdx !== -1 &&
    completeIdx !== -1 &&
    startIdx <= Math.max(1, Math.floor(messages.length * 0.1)) &&
    completeIdx >= Math.floor(messages.length * 0.8);

  if (looksChronological) return messages;

  let currentAttempt = 0;
  const annotated = messages.map((m, idx) => {
    const key = normalizeEventKey(m?.event);
    const retryRaw = m?.data?.retry;
    if (key === 'agent_execute_start') currentAttempt = 0;
    if (key === 'agent_execute' && typeof retryRaw === 'number') currentAttempt = retryRaw;

    const phase = Object.prototype.hasOwnProperty.call(phaseOrder, key) ? phaseOrder[key] : 99;
    return { m, idx, attempt: currentAttempt, phase };
  });

  return annotated
    .slice()
    .sort((a, b) => {
      if (a.attempt !== b.attempt) return a.attempt - b.attempt;
      if (a.phase !== b.phase) return a.phase - b.phase;
      return a.idx - b.idx;
    })
    .map((x) => x.m);
};

export const preprocessExecMessages = (execMessages) => {
  const raw = Array.isArray(execMessages) ? execMessages : [];
  return orderExecMessages(sliceExecutionWindow(raw));
};

export const agentMessagesHaveRetryMarkers = (messages) =>
  Array.isArray(messages) &&
  messages.some(
    (m) => normalizeEventKey(m?.event) === 'agent_execute' && typeof m?.data?.retry === 'number'
  );

/**
 * Split one agent's ordered messages into buckets per `data.retry` (from agent_execute).
 */
export const splitAgentMessagesByRetry = (messages) => {
  if (!Array.isArray(messages) || messages.length === 0) return [];
  if (!agentMessagesHaveRetryMarkers(messages)) {
    return [{ retry: 1, messages: messages.slice() }];
  }
  const byRetry = new Map();
  let activeRetry = 1;
  for (const m of messages) {
    const ev = normalizeEventKey(m?.event);
    if (ev === 'agent_execute' && typeof m?.data?.retry === 'number') {
      activeRetry = m.data.retry;
    }
    if (!byRetry.has(activeRetry)) byRetry.set(activeRetry, []);
    byRetry.get(activeRetry).push(m);
  }
  return Array.from(byRetry.entries())
    .sort((a, b) => a[0] - b[0])
    .map(([retry, msgs]) => ({ retry, messages: msgs }));
};

/** Map each message object to its retry bucket (same rules as splitAgentMessagesByRetry). */
export const buildMessageRetryMap = (messages) => {
  const map = new Map();
  if (!Array.isArray(messages) || messages.length === 0) return map;
  if (!agentMessagesHaveRetryMarkers(messages)) {
    messages.forEach((m) => map.set(m, 1));
    return map;
  }
  let activeRetry = 1;
  for (const m of messages) {
    const ev = normalizeEventKey(m?.event);
    if (ev === 'agent_execute' && typeof m?.data?.retry === 'number') {
      activeRetry = m.data.retry;
    }
    map.set(m, activeRetry);
  }
  return map;
};

const pushConfiguredLlmToolChildren = (agentNode, key, matchedAgent, segmentRetry) => {
  if (!matchedAgent) return;
  const llms = Array.isArray(matchedAgent.llms) ? matchedAgent.llms : [];
  const tools = Array.isArray(matchedAgent.tools) ? matchedAgent.tools : [];
  const seg = segmentRetry != null ? `:r${segmentRetry}` : '';

  llms.forEach((m, j) => {
    agentNode.children.push({
      id: `llm:${key}${seg}:${j}`,
      name: m,
      kind: 'LLM',
      status: 'unknown',
      durationMs: null,
      attributes: { 'llm.model': m },
      events: [],
      children: []
    });
  });

  tools.forEach((t, j) => {
    const toolName = typeof t === 'string' ? t : (t?.name || t?.handlerName || `Tool ${j + 1}`);
    let toolAttributes = {};
    if (typeof t === 'object' && t?.attributes) {
      if (Array.isArray(t.attributes)) {
        t.attributes.forEach((attr) => {
          if (typeof attr === 'object' && attr !== null) Object.assign(toolAttributes, attr);
        });
      } else if (typeof t.attributes === 'object') {
        toolAttributes = t.attributes;
      }
    }
    agentNode.children.push({
      id: `tool:${key}${seg}:${j}`,
      name: toolName,
      kind: 'Tool',
      status: 'unknown',
      durationMs: null,
      attributes: toolAttributes,
      events: [],
      children: []
    });
  });
};

const pushGoalResultToolSteps = (agentNode, key, list, segmentRetry) => {
  const seg = segmentRetry != null ? `:r${segmentRetry}` : '';
  for (const m of list) {
    const goalResult = m?.data?.goal_result || m?.data?.goalResult;
    const content = goalResult?.content;
    if (Array.isArray(content)) {
      content.forEach((iteration) => {
        if (!Array.isArray(iteration)) return;
        iteration.forEach((stepObj) => {
          const method = firstKey(stepObj);
          if (!method) return;
          const payload = stepObj?.[method];
          agentNode.children.push({
            id: `tool:${key}${seg}:${method}:${agentNode.children.length}`,
            name: method,
            kind: 'Tool',
            status: payload?.success === false ? 'error' : 'success',
            durationMs: null,
            attributes: payload || {},
            events: [],
            children: []
          });
        });
      });
    }
  }
};

const mergeDuplicateSiblingToolSpans = (rootNode) => {
  const normalize = (v) => String(v || '').trim().toLowerCase();
  const isTool = (n) => normalize(n?.kind) === 'tool';

  const mergeInto = (target, source) => {
    const tStatus = normalizeStatus(target?.status);
    const sStatus = normalizeStatus(source?.status);
    if (tStatus !== 'error' && sStatus === 'error') target.status = 'error';
    if ((tStatus === 'unknown' || tStatus === 'skipped') && (sStatus === 'success' || sStatus === 'running')) {
      target.status = sStatus;
    }
    if (typeof target?.durationMs !== 'number' && typeof source?.durationMs === 'number') {
      target.durationMs = source.durationMs;
    } else if (typeof target?.durationMs === 'number' && typeof source?.durationMs === 'number') {
      // Best-effort: keep the larger duration (often start/end span duplicates).
      target.durationMs = Math.max(target.durationMs, source.durationMs);
    }
    target.attributes = { ...(source?.attributes || {}), ...(target?.attributes || {}) };
    const tEvents = Array.isArray(target?.events) ? target.events : [];
    const sEvents = Array.isArray(source?.events) ? source.events : [];
    if (sEvents.length) target.events = tEvents.concat(sEvents);
    const tChildren = Array.isArray(target?.children) ? target.children : [];
    const sChildren = Array.isArray(source?.children) ? source.children : [];
    if (sChildren.length) target.children = tChildren.concat(sChildren);
  };

  const walk = (node) => {
    if (!node || !Array.isArray(node.children) || node.children.length === 0) return;

    // Only merge duplicate Tool siblings under the same parent.
    const firstByKey = new Map();
    const nextChildren = [];
    for (const child of node.children) {
      const key = isTool(child) ? `${normalize(child.kind)}::${normalize(child.name)}` : null;
      if (key && firstByKey.has(key)) {
        const existing = firstByKey.get(key);
        mergeInto(existing, child);
        continue;
      }
      if (key) firstByKey.set(key, child);
      nextChildren.push(child);
    }
    node.children = nextChildren;
    node.children.forEach(walk);
  };

  walk(rootNode);
};

export const buildSpanTree = ({ trace, execMessages }) => {
  // Preferred: real spans attached to trace.
  const spans = Array.isArray(trace?.spans) ? trace.spans : null;
  if (spans && spans.length) {
    const byId = new Map();
    spans.forEach((s) => {
      const id = s.id || s.span_id || s.spanId || s.name;
      if (!id) return;
      byId.set(id, {
        id,
        name: s.name || s.operation_name || 'Span',
        kind: s.kind || s.type || 'Span',
        status: normalizeStatus(s.status),
        durationMs: typeof s.durationMs === 'number' ? s.durationMs : (typeof s.duration === 'number' ? s.duration : null),
        attributes: s.attributes || {},
        events: s.events || [],
        parentId: s.parentId || s.parent_span_id || s.parentSpanId || null,
        children: []
      });
    });
    // Link
    const roots = [];
    for (const node of byId.values()) {
      if (node.parentId && byId.has(node.parentId)) byId.get(node.parentId).children.push(node);
      else roots.push(node);
    }

    // If any span has an error event, force it to error.
    for (const node of byId.values()) {
      if (hasErrorEvent(node.events)) node.status = 'error';
    }

    // If any span is error, force the synthetic root to error too.
    const hasAnyError = Array.from(byId.values()).some((n) => normalizeStatus(n.status) === 'error');
    const root = {
      id: trace?.id || 'trace',
      name: trace?.name || 'Execution',
      kind: 'Project',
      status: hasAnyError ? 'error' : normalizeStatus(trace?.status || 'success'),
      durationMs: typeof trace?.durationMs === 'number' ? trace.durationMs : (typeof trace?.duration === 'number' ? trace.duration : null),
      attributes: {},
      events: [],
      children: roots
    };
    mergeDuplicateSiblingToolSpans(root);
    return root;
  }

  // Fallback: synthesize "span-like" tree from execMessages / trace.agents.
  const msgs = Array.isArray(execMessages) ? execMessages : [];
  const ordered = preprocessExecMessages(msgs);
  const traceWfStatusRaw = trace?.wfStatus ?? trace?.wf_status ?? trace?.workflowStatus ?? trace?.workflow_status ?? null;
  const traceWfStatusNorm = normalizeStatus(traceWfStatusRaw || trace?.status || (trace?.active === true ? 'running' : null));
  const defaultAgentFallback = traceWfStatusNorm === 'running' || traceWfStatusNorm === 'pending' ? 'running' : 'success';
  const defaultEmptyAgentStatus = traceWfStatusNorm === 'running' || traceWfStatusNorm === 'pending' ? 'running' : 'skipped';
  const root = {
    id: trace?.id || 'trace',
    name: trace?.name || 'Execution',
    kind: 'Project',
    status: deriveStatusFromMessages(ordered.length ? ordered : msgs, trace?.status || 'running'),
    durationMs: typeof trace?.duration === 'number' ? trace.duration : null,
    attributes: {},
    events: [],
    children: []
  };

  // If we have workflow exec messages, group by agentName/agentId; split retries into separate nodes.
  if (ordered.length) {
    const agentConfigList = trace?.appConfig?.agentConfig ?? trace?.agentConfig ?? [];
    const normalizedConfiguredAgents = (Array.isArray(agentConfigList) ? agentConfigList : [])
      .map((cfg) => ({
        ...cfg,
        agentId: cfg?.agentId != null ? String(cfg.agentId) : null,
        title: cfg?.title != null ? String(cfg.title) : null,
        name: cfg?.name != null ? String(cfg.name) : null
      }));
    const configuredById = new Map(
      normalizedConfiguredAgents
        .filter((cfg) => Boolean(cfg.agentId))
        .map((cfg) => [cfg.agentId, cfg])
    );

    const resolveConfiguredAgentByName = (rawName) => {
      const n = String(rawName || '').trim().toLowerCase();
      if (!n) return null;
      return (
        normalizedConfiguredAgents.find((cfg) => String(cfg?.title || '').trim().toLowerCase() === n) ||
        normalizedConfiguredAgents.find((cfg) => String(cfg?.name || '').trim().toLowerCase() === n) ||
        null
      );
    };

    const byAgent = new Map();
    for (const m of ordered) {
      const msgAgentId = m?.agentId != null ? String(m.agentId) : null;
      const msgAgentName = m?.agentName != null ? String(m.agentName) : '';
      const matchedById = msgAgentId ? configuredById.get(msgAgentId) || null : null;
      const matchedByName = !matchedById ? resolveConfiguredAgentByName(msgAgentName) : null;

      // Prefer configured agent id/name to avoid creating duplicate synthetic rows.
      const effectiveAgentId = matchedById?.agentId || matchedByName?.agentId || msgAgentId;
      const effectiveAgentName =
        matchedById?.title ||
        matchedById?.name ||
        matchedByName?.title ||
        matchedByName?.name ||
        msgAgentName ||
        effectiveAgentId ||
        'Agent';

      // If workflow has configured agents, skip unknown messages that cannot be mapped.
      if (normalizedConfiguredAgents.length > 0 && !effectiveAgentId) {
        continue;
      }

      const key = `${effectiveAgentId || effectiveAgentName}::${effectiveAgentName}`;
      const list = byAgent.get(key) || [];
      list.push(m);
      byAgent.set(key, list);
    }

    const orderIndexByAgentId = new Map(
      (Array.isArray(agentConfigList) ? agentConfigList : [])
        .map((cfg, idx) => [cfg?.agentId, idx])
        .filter(([agentId]) => Boolean(agentId))
    );

    // Ensure all configured agents appear, even if they have zero exec messages.
    const configuredKeys = (Array.isArray(agentConfigList) ? agentConfigList : [])
      .map((cfg) => {
        const agentId = cfg?.agentId;
        if (!agentId) return null;
        const agentName = cfg?.title || cfg?.name || cfg?.agentName || agentId;
        return `${agentId}::${agentName}`;
      })
      .filter(Boolean);

    for (const k of configuredKeys) {
      if (!byAgent.has(k)) byAgent.set(k, []);
    }

    const sortedKeys = Array.from(byAgent.keys()).sort((a, b) => {
      const [agentIdA, nameA] = a.split('::');
      const [agentIdB, nameB] = b.split('::');
      const ia = orderIndexByAgentId.get(agentIdA);
      const ib = orderIndexByAgentId.get(agentIdB);

      if (typeof ia === 'number' && typeof ib === 'number') return ia - ib;
      if (typeof ia === 'number') return -1;
      if (typeof ib === 'number') return 1;
      return String(nameA || '').localeCompare(String(nameB || ''));
    });

    // Used for attaching configured LLMs/tools under each agent node.
    const configuredAgents = Array.isArray(trace?.agents) ? trace.agents : [];

    let agentIndex = 0;
    for (const key of sortedKeys) {
      const list = byAgent.get(key);
      const [, agentName] = key.split('::');
      const segments =
        Array.isArray(list) && list.length > 0
          ? splitAgentMessagesByRetry(list)
          : [{ retry: 1, messages: [] }];
      const multiRetry = segments.length > 1;

      agentIndex += 1;

      const matchedAgent =
        configuredAgents.find((a) => typeof a === 'object' && a?.name === agentName) || null;

      for (const segment of segments) {
        const { retry, messages: segMsgs } = segment;
        const displayName = agentName;

        const agentNode = {
          id: `agent:${key}:retry:${retry}`,
          name: displayName,
          kind: 'Agent',
          status:
            Array.isArray(segMsgs) && segMsgs.length > 0
              ? deriveStatusFromMessages(segMsgs, defaultAgentFallback)
              : defaultEmptyAgentStatus,
          durationMs: null,
          attributes: {
            requestId: segMsgs?.[0]?.requestId,
            agentId: segMsgs?.[0]?.agentId || key.split('::')[0],
            pipeId: segMsgs?.[0]?.pipeId,
            agentName,
            retry,
            hasMultipleRetries: multiRetry
          },
          events: segMsgs.map((m) => m?.event).filter(Boolean).map((e) => ({ name: e })),
          children: []
        };

        pushConfiguredLlmToolChildren(agentNode, key, matchedAgent, retry);
        pushGoalResultToolSteps(agentNode, key, segMsgs, retry);

        root.children.push(agentNode);
      }
    }

    if (root.children.some((c) => normalizeStatus(c.status) === 'error')) {
      root.status = 'error';
    }
    mergeDuplicateSiblingToolSpans(root);
    return root;
  }

  // Final fallback: use trace.agents array
  const agents = Array.isArray(trace?.agents) ? trace.agents : [];
  agents.forEach((a, idx) => {
    const name = typeof a === 'string' ? a : a?.name || `Agent ${idx + 1}`;
    const tools = typeof a === 'object' && Array.isArray(a.tools) ? a.tools : [];
    const llms = typeof a === 'object' && Array.isArray(a.llms) ? a.llms : [];
    root.children.push({
      id: `agent:${idx}`,
      name,
      kind: 'Agent',
      status: defaultEmptyAgentStatus,
      durationMs: null,
      attributes: {},
      events: [],
      children: [
        ...llms.map((m, j) => ({
          id: `llm:${idx}:${j}`,
          name: m,
          kind: 'LLM',
          status: defaultEmptyAgentStatus,
          durationMs: null,
          attributes: { 'llm.model': m },
          events: [],
          children: []
        })),
        ...tools.map((t, j) => {
          // Handle both string tools and object tools with name/handlerName
          const toolName = typeof t === 'string' ? t : (t?.name || t?.handlerName || `Tool ${j + 1}`);
          // Extract attributes from tool object if available
          // handlerConfig.attribute is an array like [{ llm: "AzureOpenAI", ... }]
          let toolAttributes = {};
          if (typeof t === 'object' && t?.attributes) {
            if (Array.isArray(t.attributes)) {
              // Flatten array of attribute objects into a single object
              t.attributes.forEach(attr => {
                if (typeof attr === 'object' && attr !== null) {
                  Object.assign(toolAttributes, attr);
                }
              });
            } else if (typeof t.attributes === 'object') {
              toolAttributes = t.attributes;
            }
          }
          return {
            id: `tool:${idx}:${j}`,
            name: toolName,
            kind: 'Tool',
            status: 'success',
            durationMs: null,
            attributes: toolAttributes,
            events: [],
            children: []
          };
        })
      ]
    });
  });

  mergeDuplicateSiblingToolSpans(root);
  return root;
};

export const flattenSpanTree = (root) => {
  const out = [];
  const dfs = (node, parent = null, depth = 0) => {
    out.push({ node, parent, depth });
    (node.children || []).forEach((c) => dfs(c, node, depth + 1));
  };
  dfs(root);
  return out;
};

export const summarizeTraceMetrics = (root, trace) => {
  const flat = flattenSpanTree(root);
  const spansCount = Math.max(0, flat.length - 1);

  let durationMs = null;
  if (typeof trace?.durationMs === 'number') durationMs = trace.durationMs;
  else if (typeof trace?.duration === 'number') durationMs = trace.duration;
  else if (typeof root?.durationMs === 'number') durationMs = root.durationMs;

  // Token/cost aggregation (best-effort)
  let tokens = trace?.tokens ?? trace?.tokenCount ?? null;
  let cost = trace?.cost ?? null;
  let inputTokens = null;
  let outputTokens = null;

  for (const { node } of flat) {
    const attrs = node?.attributes || {};
    const it = attrs['llm.input_tokens'] ?? attrs.input_tokens ?? attrs.inputTokens;
    const ot = attrs['llm.output_tokens'] ?? attrs.output_tokens ?? attrs.outputTokens;
    if (typeof it === 'number') inputTokens = (inputTokens || 0) + it;
    if (typeof ot === 'number') outputTokens = (outputTokens || 0) + ot;
    const c = attrs.cost ?? attrs['llm.cost'];
    if (typeof c === 'number') cost = (cost || 0) + c;
  }

  if (typeof inputTokens === 'number' || typeof outputTokens === 'number') {
    tokens = (inputTokens || 0) + (outputTokens || 0);
  }

  return { durationMs, spansCount, tokens, cost, inputTokens, outputTokens };
};

export const unwrapWorkflowMessage = (message) => {
  if (!message) return null;
  return message?.ninfo?.msg ?? message?.data ?? message;
};

export const resolveExecutionIdFromMessage = (message) => {
  const payload = unwrapWorkflowMessage(message);
  return (
    payload?.executionId ||
    payload?.workflowExecutionId ||
    payload?.workflow_execution_id ||
    payload?.requestId ||
    payload?.data?.requestId ||
    message?.executionId ||
    message?.workflowExecutionId ||
    message?.requestId ||
    null
  );
};

export const messageMatchesTraceExecution = (message, trace) => {
  const traceExecutionId =
    trace?.executionId || trace?.workflowExecutionId || trace?.id || null;
  if (!traceExecutionId) return true;

  const msgExecId = resolveExecutionIdFromMessage(message);
  if (!msgExecId) return true;

  const traceIds = new Set(
    [
      traceExecutionId,
      trace?.requestId,
      trace?.attributes?.requestId,
      trace?.data?.requestId
    ]
      .filter(Boolean)
      .map(String)
  );
  return traceIds.has(String(msgExecId));
};

export const resolveWfStatusFromMessage = (message) => {
  const payload = unwrapWorkflowMessage(message);
  const raw =
    payload?.wfStatus ||
    payload?.wf_status ||
    payload?.workflowStatus ||
    payload?.workflow_status ||
    message?.ninfo?.wfStatus ||
    message?.ninfo?.wf_status ||
    message?.ninfo?.status ||
    message?.wfStatus ||
    message?.wf_status ||
    null;
  if (raw) return String(raw).toUpperCase();
  const status = payload?.status || payload?.execStatus || message?.status || message?.execStatus;
  if (!status) return null;
  const s = String(status).toUpperCase();
  if (['RUNNING', 'FAILED', 'ERROR', 'SUCCESS', 'COMPLETED', 'PENDING'].includes(s)) {
    return s;
  }
  return null;
};

export const resolveAgentIdFromMessage = (message) => {
  const payload = unwrapWorkflowMessage(message);
  return (
    payload?.agentId ||
    payload?.agent_id ||
    payload?.data?.agentId ||
    payload?.data?.agent_id ||
    message?.agentId ||
    message?.agent_id ||
    null
  );
};

export const deriveAgentStatusFromExecMessage = (msg) => {
  if (!msg) return null;
  const eventName = String(msg?.event || '').toLowerCase();
  if (eventName === 'agent_execute_error' || eventName === 'request_failed') return 'error';
  if (
    eventName === 'agent_execute_complete' ||
    eventName === 'agent_goal_satisfied' ||
    eventName === 'goal_satisfied' ||
    eventName === 'agent_iteration_complete'
  ) {
    return 'success';
  }
  if (
    eventName === 'agent_execute_start' ||
    eventName === 'agent_execute' ||
    eventName === 'agent_llm_execute' ||
    eventName === 'agent_verify_goal_usage'
  ) {
    return 'running';
  }
  const status = String(msg?.execStatus || msg?.status || '').toLowerCase();
  if (status === 'error' || status === 'failed') return 'error';
  if (status === 'success' || status === 'completed') return 'success';
  if (status === 'running') return 'running';
  return null;
};

export const deriveWfStatusFromExecMessage = (msg) => {
  const explicit = resolveWfStatusFromMessage(msg);
  if (explicit) return explicit;

  const eventName = String(msg?.event || '').toLowerCase();
  if (eventName === 'request_failed' || eventName === 'agent_execute_error') return 'FAILED';
  if (eventName === 'request_completed') return 'SUCCESS';

  const agentStatus = deriveAgentStatusFromExecMessage(msg);
  if (agentStatus === 'error') return 'FAILED';
  if (agentStatus === 'success') return 'SUCCESS';
  if (agentStatus === 'running') return 'RUNNING';
  return null;
};

export const deriveWfStatusFromExecMessages = (messages = []) => {
  const list = Array.isArray(messages) ? messages : [];
  if (!list.length) return null;

  let latest = null;
  for (const msg of list) {
    const next = deriveWfStatusFromExecMessage(msg);
    if (!next) continue;
    latest = next;
    if (isTerminalWfStatus(next)) return next;
  }
  return latest;
};

export const deriveWfStatusFromAgentStatuses = (statusesByKey = {}) => {
  const statuses = Object.values(statusesByKey || {}).filter(Boolean);
  if (!statuses.length) return null;
  if (statuses.some((s) => s === 'error')) return 'FAILED';
  if (statuses.every((s) => s === 'success')) return 'SUCCESS';
  if (statuses.some((s) => s === 'running')) return 'RUNNING';
  return null;
};

export const resolveHeaderWfStatus = ({
  liveWfStatus = null,
  traceWfStatus = null,
  execMessages = [],
  agentStatusesByScopeKey = {}
} = {}) => {
  const fromMessages = deriveWfStatusFromExecMessages(execMessages);
  const fromAgents = deriveWfStatusFromAgentStatuses(agentStatusesByScopeKey);
  const derived = fromMessages || fromAgents;

  if (derived && isTerminalWfStatus(derived)) return derived;
  if (liveWfStatus) return liveWfStatus;
  if (derived) return derived;
  return traceWfStatus || null;
};

export const isTerminalWfStatus = (status) => {
  const s = String(status || '').toUpperCase();
  return ['SUCCESS', 'COMPLETED', 'FAILED', 'ERROR'].includes(s);
};


